import L from 'leaflet'
import { api, type MapSettings } from './api'

/** What is drawn UNDER a map's markers -- OpenStreetMap tiles or a Google map --
 *  for all three maps: the Take a call pin (`PinMap`), the working call and
 *  Past calls (`PlaceMap`), and the vehicles (`FleetMap`).
 *
 *  THE MAP IS STILL LEAFLET ON EVERY LEVEL
 *
 *  Level B (full Google) draws Google's own roadmap, through
 *  `leaflet.gridlayer.googlemutant`: a real `google.maps.Map` renders in a
 *  hidden box inside the Leaflet map and its tiles are copied into a Leaflet
 *  layer. So the draggable pin, the vehicle pins and their tooltips,
 *  `fitBounds` and the teardown are the SAME code on every level. Drawing a
 *  native Google map instead would be each of the three maps written twice,
 *  and two copies of a map that has to agree about where a call is.
 *
 *  A GOOGLE MAP THAT CANNOT BE DRAWN IS AN OPENSTREETMAP ONE, AND SAYS SO
 *
 *  No key, a script that fails or takes too long, Google refusing the key, or
 *  a Google map that loads and never draws a tile: every one of them ends in
 *  the organization's own OpenStreetMap tiles plus one sentence under the map. Never a blank square -- a dispatcher reads that
 *  as "still loading" and waits -- and never an exception out of a React
 *  effect, which takes the dispatch screen with it. That is why `tile_url` is
 *  still required on level B: it is the fallback. CLAUDE.md: "No dependency
 *  may block the screen from loading."
 *
 *  ONE ATTEMPT PER PAGE
 *
 *  Google's loader is meant to be included once per page and cannot be loaded
 *  again with a different key, so the script, the plugin and the deadline are
 *  one shared promise. Three maps opening at once wait on the same load, and a
 *  load that failed is failed for every map on the page until a reload -- the
 *  alternative is each new call's map waiting out the same dead network again,
 *  mid-call, and one screen showing two different maps.
 *
 *  THE KEY IS NEVER LOGGED. It is public by design (Google restricts it to this
 *  site's hostnames, not to secrecy) and it is still not ours to print: nothing
 *  here writes it anywhere but the script URL, and no error carries it. */

/** Fetched once per page load and shared. The tile URL does not change between
 *  two cards on one screen, and three cards asking separately is three requests
 *  for one answer. It lived in each of the three maps until the level arrived
 *  -- three caches of one answer. A level changed in the console therefore
 *  takes effect on each dispatcher's next reload, like every other setting a
 *  screen reads once. */
let cached: Promise<MapSettings> | null = null

export function mapSettings(): Promise<MapSettings> {
  if (!cached) {
    cached = api.mapSettings().catch(() => {
      cached = null
      return { enabled: false, tile_url: null, attribution: null, centre: null }
    })
  }
  return cached
}

const NO_KEY = 'No Google map key is set — showing OpenStreetMap.'
const FAILED = 'Google Maps did not load — showing OpenStreetMap.'
const REFUSED = 'Google refused this site’s map key — showing OpenStreetMap.'

/** Google's plugin waits ten seconds for the API before giving up, so this is
 *  the same ten. A dispatcher looking at a grey box longer than that has
 *  stopped looking. */
const WAIT_MS = 10_000

const SCRIPT = 'https://maps.googleapis.com/maps/api/js'
const READY = '__advCrmGoogleMapsReady'

type Mutant = typeof import('leaflet.gridlayer.googlemutant/src/Leaflet.GoogleMutant.mjs').default

type GoogleNamespace = {
  maps: {
    importLibrary: (name: string) => Promise<unknown>
    Map?: new (box: HTMLElement, options: Record<string, unknown>) => unknown
    ControlPosition?: { BOTTOM_RIGHT: number }
    event?: {
      addListenerOnce: (target: unknown, name: string, fn: () => void) => unknown
    }
  }
}

declare global {
  interface Window {
    /** GOOGLE'S ONLY REPORT THAT IT REFUSED THE KEY. A refused key does not
     *  fail to load: Google draws its own error, or a dark map watermarked
     *  "for development purposes only", calls this global and nothing else --
     *  and only once the map has been painted, which a background tab never
     *  does. */
    gm_authFailure?: () => void
  }
}

function googleIn(win: Window): GoogleNamespace | undefined {
  return (win as unknown as { google?: GoogleNamespace }).google
}

function scriptUrl(key: string): string {
  const query = new URLSearchParams({
    key, loading: 'async', callback: READY, v: 'weekly',
  })
  return `${SCRIPT}?${query}`
}

let ready: Promise<Mutant> | null = null
let readyKey: string | null = null
/** WHY GOOGLE IS OFF FOR THE REST OF THIS PAGE, once it is. Set by
 *  `gm_authFailure` -- which can arrive AFTER a map has drawn, so it also
 *  switches every map already showing Google, through `live` -- and by a map
 *  that loaded and never drew a tile. A map opened afterwards goes straight to
 *  OpenStreetMap with this sentence rather than waiting out the same failure. */
let broken: string | null = null
const live = new Set<() => void>()

/** The Maps JavaScript API and the plugin, loaded once, within `WAIT_MS`. */
function loadGoogle(key: string): Promise<Mutant> {
  if (ready) {
    // One page is one organization, so a second key does not happen. If it
    // ever did, it is a map that cannot be drawn, not a second script tag.
    return readyKey === key ? ready : Promise.reject(new Error('a second key'))
  }
  readyKey = key

  const script = new Promise<void>((resolve, reject) => {
    const earlier = window.gm_authFailure
    window.gm_authFailure = () => {
      broken = REFUSED
      for (const fallBack of [...live]) fallBack()
      earlier?.()
    }
    ;(window as unknown as Record<string, unknown>)[READY] = () => {
      const google = googleIn(window)
      if (!google) return reject(new Error('no google.maps'))
      // `loading=async` defines the namespace and nothing in it: `Map`,
      // `LatLng` and `event`, all of which the plugin uses, exist only once
      // their libraries are imported.
      Promise.all([google.maps.importLibrary('core'), google.maps.importLibrary('maps')])
        .then(() => {
          if (typeof googleIn(window)?.maps.Map === 'function') resolve()
          else reject(new Error('no google.maps.Map'))
        })
        .catch(() => reject(new Error('library import failed')))
    }
    const tag = document.createElement('script')
    tag.src = scriptUrl(key)
    tag.async = true
    tag.onerror = () => reject(new Error('script failed'))
    document.head.appendChild(tag)
  })

  // ONLY AFTER THE API EXISTS. The plugin polls for `google.maps.Map` for ten
  // seconds and then THROWS inside a timer, where nothing can catch it; adding
  // the layer before the API is there is how that throw happens.
  const plugin = import('leaflet.gridlayer.googlemutant/src/Leaflet.GoogleMutant.mjs')
    .then((m) => m.default)

  ready = new Promise<Mutant>((resolve, reject) => {
    const deadline = setTimeout(() => reject(new Error('timed out')), WAIT_MS)
    Promise.all([script, plugin]).then(
      ([, Layer]) => { clearTimeout(deadline); resolve(Layer) },
      (e) => { clearTimeout(deadline); reject(e) },
    )
  })
  // Every map handles the rejection itself. This stops the shared promise from
  // being reported as unhandled when the only map waiting has since closed.
  ready.catch(() => {})
  return ready
}

/** Tag Google's credit box so `dispatch.css` leaves it alone.
 *
 *  The plugin moves Google's "Map data ©" box into Leaflet's corner and gives
 *  it Leaflet's `leaflet-control-attribution` class, which the dark theme
 *  re-skins at 10px on the panel colour. Google's terms want that credit
 *  "clear, legible, unmodified". This listener is registered before the
 *  plugin's own, so the class is there by the time the box is placed. */
function markCredit(e: L.LeafletEvent): void {
  try {
    const positions = (e as unknown as { positions?: { get(at: number): unknown } }).positions
    const at = googleIn(window)?.maps.ControlPosition?.BOTTOM_RIGHT
    const box = at === undefined ? null : positions?.get(at)
    if (box instanceof HTMLElement) box.classList.add('google-credit')
  } catch {
    // The credit keeps Leaflet's styling. Nothing on the map depends on it.
  }
}

/** How often a Google layer that has not drawn a tile yet is looked at. After
 *  `WAIT_MS` of those looks while it could be seen, it is one that cannot. */
const TICK_MS = 1_000

function quietly(fn: () => void): void {
  try {
    fn()
  } catch {
    // A map torn down underneath a late callback. There is nothing left to
    // draw on, and an exception here would land in a React effect.
  }
}

/** Add the base layer the settings ask for, and return `detach`.
 *
 *  `onNote` is handed the one sentence to show under the map, or null. Call
 *  `detach()` BEFORE `map.remove()`: it stops a load still in flight from
 *  drawing onto a map that is gone, and takes the map off the list a refused
 *  key switches.
 *
 *  The caller decides whether to draw a map at all, from `enabled` and
 *  `tile_url`, exactly as before; this only decides what is under it. */
export function addBasemap(
  map: L.Map,
  settings: MapSettings,
  onNote: (note: string | null) => void,
): () => void {
  let dead = false
  let layer: L.Layer | null = null
  let fellBack = false
  let watchdog: ReturnType<typeof setInterval> | undefined

  const osm = (note: string | null) => {
    if (settings.tile_url) {
      L.tileLayer(settings.tile_url, {
        maxZoom: 19,
        // Required by OpenStreetMap and stored with the tile URL, so switching
        // provider cannot leave the wrong credit under the map. On Google the
        // plugin brings Google's own credit instead, so this is not added.
        attribution: settings.attribution || '',
      }).addTo(map)
    }
    onNote(note)
  }

  const letGo = () => {
    clearInterval(watchdog)
    live.delete(onRefused)
    const drawn = layer
    layer = null
    if (drawn) quietly(() => { map.removeLayer(drawn) })
  }
  const fallBack = (note: string) => {
    if (dead || fellBack) return
    fellBack = true
    letGo()
    quietly(() => osm(note))
  }
  const onRefused = () => fallBack(REFUSED)
  const detach = () => {
    dead = true
    letGo()
  }

  const key = settings.google_browser_key
  if (settings.base !== 'google') {
    // Level A, level A with Google search, a server from before 056 that
    // sends no `base` -- and level B when the server already knows it cannot
    // draw Google, in which case `base_note` says why.
    osm(settings.base_note || null)
    return detach
  }
  if (!key) {
    osm(settings.base_note || NO_KEY)
    return detach
  }
  if (broken) {
    osm(broken)
    return detach
  }

  loadGoogle(key)
    .then((GoogleLayer) => {
      if (dead || fellBack) return
      // The refusal can land between the load and here.
      if (broken) return fallBack(broken)
      // THE OPTIONS ARE PASSED, NOT DEFAULTED. The plugin sets its defaults in
      // a static block that Leaflet 1 never reads; without `type` the hidden
      // Google map has no map type at all.
      const google = new GoogleLayer({ type: 'roadmap', maxZoom: 21, maxNativeZoom: 21 })
      google.on('controls_ready', markCredit)

      // A LOADED API IS NOT A DRAWN MAP. Google reports a refused key through
      // `gm_authFailure` only once the map has been painted, and the plugin
      // finds Google's tiles by the shape of their URLs -- so a change on
      // Google's side is a grey square with a pin on it, forever, and no error
      // anywhere. One tile is proof it draws. The clock only runs while the
      // map can be seen and has a size: a background tab paints nothing and
      // loads no tiles, and that is not Google failing.
      let seen = 0
      google.once('tileload', () => clearInterval(watchdog))
      watchdog = setInterval(() => {
        const box = map.getContainer()
        if (document.visibilityState !== 'visible') return
        if (!box.clientWidth || !box.clientHeight) return
        seen += TICK_MS
        if (seen < WAIT_MS) return
        broken = broken ?? FAILED
        fallBack(broken)
      }, TICK_MS)

      layer = google
      google.addTo(map)
      live.add(onRefused)
      onNote(null)
    })
    // A failed load, a timeout, a second key, or the layer throwing as it was
    // added -- all of them the same answer for the dispatcher.
    .catch(() => fallBack(FAILED))

  return detach
}

/** Try a browser key from THIS page's own browser, for the super admin console.
 *
 *  THE SERVER CANNOT TEST IT. A browser key is restricted by website, so a
 *  request from the CRM server is refused whether the key is right or not. The
 *  console runs at admin.crm.advancetalk.ca, inside the `*.crm.advancetalk.ca`
 *  restriction the dispatcher screens are under, so its own browser is the
 *  representative place to ask.
 *
 *  IN A FRESH, HIDDEN FRAME PER TEST. Google's script loads once per window
 *  and keeps the first key, so testing in this window would test one key
 *  forever -- and would load Google into the console for nothing. `srcdoc`
 *  and not `about:blank`, because a srcdoc frame's requests carry the parent
 *  page as their referrer and a blank frame's carry none, which Google refuses.
 *
 *  INVISIBLE, NOT OFF-SCREEN. Google only decides about the key once the map
 *  has been painted, and a browser may stop painting a frame it has placed
 *  outside the window -- so the frame sits in the window at zero opacity,
 *  where a far-left frame would time out and blame the network.
 *
 *  A key that loads and draws is reported good only after a short grace: a
 *  refusal is reported by `gm_authFailure`, which can arrive after the first
 *  tiles. This costs one Google map load per press. */
export function testBrowserKey(key: string): Promise<{ ok: boolean; detail: string }> {
  return new Promise((resolve) => {
    const frame = document.createElement('iframe')
    frame.setAttribute('aria-hidden', 'true')
    frame.tabIndex = -1
    frame.style.cssText =
      'position:fixed;left:0;top:0;width:320px;height:240px;border:0;'
      + 'opacity:0;pointer-events:none;z-index:-1'

    let done = false
    let grace: ReturnType<typeof setTimeout> | undefined
    const finish = (ok: boolean, detail: string) => {
      if (done) return
      done = true
      clearTimeout(deadline)
      clearTimeout(grace)
      frame.remove()
      resolve({ ok, detail })
    }
    const deadline = setTimeout(() => finish(false,
      'Google Maps did not answer within ten seconds, so the key could not be '
      + 'judged. Check that this network can reach maps.googleapis.com, keep '
      + 'this window in front, and try again.'), WAIT_MS)

    frame.onload = () => {
      try {
        const win = frame.contentWindow
        const doc = frame.contentDocument
        const box = doc?.getElementById('m')
        if (!win || !doc || !box) return finish(false, 'The test could not be started.')

        win.gm_authFailure = () => finish(false,
          'Google refused this key. Check its website restriction includes '
          + '*.crm.advancetalk.ca and that the Maps JavaScript API is enabled; '
          + 'the browser console names the exact error.')
        ;(win as unknown as Record<string, unknown>)[READY] = () => {
          const google = googleIn(win)
          if (!google) return finish(false, 'Google Maps did not load.')
          Promise.all([google.maps.importLibrary('core'), google.maps.importLibrary('maps')])
            .then(() => {
              const g = googleIn(win)
              if (!g?.maps.Map || !g.maps.event) return finish(false, 'Google Maps did not load.')
              const drawn = new g.maps.Map(box, {
                center: { lat: 45.56, lng: -73.7 }, zoom: 12, disableDefaultUI: true,
              })
              g.maps.event.addListenerOnce(drawn, 'tilesloaded', () => {
                grace = setTimeout(() => finish(true,
                  'Google drew a map with this key from this site.'), 2_000)
              })
            })
            .catch(() => finish(false, 'Google Maps did not load.'))
        }
        const tag = doc.createElement('script')
        tag.src = scriptUrl(key)
        tag.async = true
        tag.onerror = () => finish(false,
          'Google Maps did not load. Check that this network can reach '
          + 'maps.googleapis.com.')
        doc.head.appendChild(tag)
      } catch {
        finish(false, 'The test could not be started.')
      }
    }
    frame.srcdoc =
      '<!doctype html><html><head></head><body style="margin:0">'
      + '<div id="m" style="width:320px;height:240px"></div></body></html>'
    document.body.appendChild(frame)
  })
}
